import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router";

export interface PaletteItem {
  id: string;
  label: string;
  sub?: string;
  kind: string;
  to?: string;
  run?: () => void;
}

function fold(s: string): string {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

/** Starts of words first, then anywhere; the order given breaks ties. */
function rank(items: PaletteItem[], query: string): PaletteItem[] {
  const q = fold(query.trim());
  if (!q) return items;
  const scored: { item: PaletteItem; score: number; i: number }[] = [];
  items.forEach((item, i) => {
    const text = fold(item.label + " " + (item.sub ?? ""));
    const label = fold(item.label);
    const score = label.startsWith(q) ? 0 : text.split(/[\s·/,-]+/).some((w) => w.startsWith(q)) ? 1 : text.includes(q) ? 2 : -1;
    if (score >= 0) scored.push({ item, score, i });
  });
  return scored.sort((a, b) => a.score - b.score || a.i - b.i).map((s) => s.item);
}

/** ⌘K (DESIGN §16.6): type to find an athlete, a week, a lift or a place; Enter goes there. */
export function Palette({ open, onClose, items }: { open: boolean; onClose: () => void; items: PaletteItem[] }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const shown = useMemo(() => rank(items, query).slice(0, 40), [items, query]);

  // Focus moves in the same frame it opens, or the keys typed next would land on the page (the Plan's grid types into a cell).
  useLayoutEffect(() => {
    if (!open) return;
    setQuery("");
    setActive(0);
    const back = document.activeElement as HTMLElement | null;
    input.current?.focus();
    return () => back?.focus?.();
  }, [open]);
  useEffect(() => setActive(0), [query]);
  useEffect(() => {
    list.current?.querySelector<HTMLElement>(`[data-i="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active]);

  if (!open) return null;
  const go = (item: PaletteItem | undefined) => {
    if (!item) return;
    onClose();
    if (item.run) item.run();
    else if (item.to) navigate(item.to);
  };
  const listID = "k-palette-list";
  return createPortal(
    <div
      className="k-palette-backdrop"
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="k-palette" role="dialog" aria-modal="true" aria-label="Go to">
        <input
          ref={input}
          autoFocus
          role="combobox"
          aria-expanded="true"
          aria-controls={listID}
          aria-activedescendant={shown[active] ? `k-palette-${active}` : undefined}
          aria-autocomplete="list"
          placeholder="Go to an athlete, week, lift or page…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setActive((a) => Math.min(shown.length - 1, a + 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setActive((a) => Math.max(0, a - 1));
            } else if (e.key === "Enter") {
              e.preventDefault();
              go(shown[active]);
            } else if (e.key === "Escape") {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }
          }}
        />
        <ul id={listID} role="listbox" ref={list} aria-label="Results">
          {shown.length === 0 ? (
            <li className="k-muted" aria-disabled="true">
              Nothing matches.
            </li>
          ) : (
            shown.map((item, i) => (
              <li
                key={item.id}
                id={`k-palette-${i}`}
                data-i={i}
                role="option"
                aria-selected={i === active}
                onPointerMove={() => setActive(i)}
                onClick={() => go(item)}
              >
                <span>
                  {item.label}
                  {item.sub ? <span className="k-sub">{item.sub}</span> : null}
                </span>
                <span className="k-palette-kind">{item.kind}</span>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>,
    document.body,
  );
}
